import { Badge, Box, Card, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowRightIcon,
  Bot,
  Database,
  EyeIcon,
  FileCodeIcon,
  FlaskConicalIcon,
  type LucideIcon,
  NetworkIcon,
  Radio,
  RepeatIcon,
  RocketIcon,
  ServerIcon,
  ShieldCheckIcon,
  ShieldIcon,
} from 'lucide-react'

import { InsightCard, PageHeader, RevealStack, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/hivemqagentic')({
  component: HiveMQAgenticPage,
})

interface CapabilityCardProps {
  accent: string
  colorPalette: string
  icon: LucideIcon
  title: string
  description: string
}

const capabilities: CapabilityCardProps[] = [
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: ServerIcon,
    title: 'Runs where the data lives',
    description:
      'On-prem, edge-first, behind your firewall. Outbound-only, certificate-authenticated. Designed for segmented networks.',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: FileCodeIcon,
    title: 'Authored in YAML by domain experts',
    description:
      'A single file describes what the agent senses, reasons, does, and where humans approve. Chat-driven builder optional.',
  },
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: Radio,
    title: 'Native MQTT, end to end',
    description:
      'Agents speak the protocol your OT stack already uses. Retained messages, ACLs, wildcards, and fan-out are first-class.',
  },
  {
    accent: 'orange.solid',
    colorPalette: 'orange',
    icon: NetworkIcon,
    title: 'Grounded in your industrial context',
    description:
      'ISA-95 hierarchies, OPC-UA browse trees, equipment models. The LLM reasons over what tags mean, not just their names.',
  },
  {
    accent: 'teal.solid',
    colorPalette: 'teal',
    icon: ShieldCheckIcon,
    title: 'Cryptographically auditable',
    description:
      'Tamper-evident Merkle-tree decision history, hash-chained and replayable. Built for EU AI Act, 21 CFR Part 11, and GxP.',
  },
  {
    accent: 'cyan.solid',
    colorPalette: 'cyan',
    icon: RepeatIcon,
    title: 'Structured cognition, consistent behavior',
    description:
      'A four-stage loop, payload normalization, policy gates, and replay turn probabilistic LLMs into consistent operational behavior.',
  },
]

interface LoopStageCardProps {
  icon: LucideIcon
  step: string
  title: string
  description: string
}

function LoopStageCard({ icon: Icon, step, title, description }: LoopStageCardProps) {
  return (
    <Card.Root
      flex="1"
      minW="220px"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
      h="100%"
    >
      <Card.Body p={5}>
        <VStack align="start" gap={3}>
          <HStack justify="space-between" w="100%">
            <Box p={2} bg="brand.solid" borderRadius="lg">
              <Icon size={20} />
            </Box>
            <Text fontSize="xs" color="content.tertiary" fontWeight="bold">
              {step}
            </Text>
          </HStack>
          <VStack align="start" gap={1}>
            <Text fontWeight="bold">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

function LoopArrow() {
  return (
    <Box
      display={{ base: 'none', md: 'flex' }}
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      color="content.tertiary"
    >
      <ArrowRightIcon size={20} />
    </Box>
  )
}

const outerLoop: LoopStageCardProps[] = [
  {
    icon: Radio,
    step: 'STEP 01',
    title: 'Sense',
    description:
      'Windowed inputs are distilled before reaching Reason; each Sense window runs its own clock.',
  },
  {
    icon: Bot,
    step: 'STEP 02',
    title: 'Reason',
    description: 'Interpret, plan, and validate the next action (expands into its own inner loop).',
  },
  {
    icon: RocketIcon,
    step: 'STEP 03',
    title: 'Actuate',
    description: 'Carry out the authorized action back into the operation.',
  },
  {
    icon: RepeatIcon,
    step: 'STEP 04',
    title: 'Reflect',
    description: 'Update long-term memory and per-agent state every cycle.',
  },
]

const innerLoop: LoopStageCardProps[] = [
  {
    icon: EyeIcon,
    step: 'A',
    title: 'Analyse',
    description: 'Interpret what changed and how it relates to current state.',
  },
  {
    icon: FileCodeIcon,
    step: 'B',
    title: 'Plan',
    description:
      'Choose an action consistent with the mission; LLM reasoning grounded in plant context.',
  },
  {
    icon: ShieldIcon,
    step: 'C',
    title: 'Govern',
    description: 'Apply safety, compliance, and policy rules - block, allow, or downgrade.',
  },
  {
    icon: ShieldCheckIcon,
    step: 'D',
    title: 'Authorise',
    description:
      'Confirm permission; out-of-lane actions raise a human-in-the-loop request and pause the cycle.',
  },
]

interface DevCycleCardProps {
  icon: LucideIcon
  title: string
  description: string
}

const devCycle: DevCycleCardProps[] = [
  {
    icon: FileCodeIcon,
    title: 'Build',
    description:
      'Author the agent in YAML. Start from a template, use the chat-driven builder, or write it directly.',
  },
  {
    icon: FlaskConicalIcon,
    title: 'Test',
    description:
      'Validate in a hosted sandbox with simulated inputs. Save and run locally on the community runtime.',
  },
  {
    icon: RocketIcon,
    title: 'Deploy',
    description:
      'Run inside your network on Kubernetes, Docker, or standalone. Roll out new versions without restart.',
  },
  {
    icon: ShieldIcon,
    title: 'Govern',
    description:
      'Set autonomy lanes, human-in-the-loop gates, RBAC, and just-in-time skill loading. The runtime enforces what you author.',
  },
  {
    icon: EyeIcon,
    title: 'Observe',
    description:
      'Watch the fleet: per-agent state & traces, fleet broadcasts, cryptographic audit trail, full decision replay.',
  },
]

interface PlatformLayerProps {
  badge: string
  icon: LucideIcon
  title: string
  quote: string
  items: string[]
  highlighted?: boolean
}

const platformLayers: PlatformLayerProps[] = [
  {
    badge: 'Layer 3',
    icon: Bot,
    title: 'Agentic AI for Operations',
    quote: 'We turn it into safe action.',
    items: [
      'YAML-authored agents',
      'Sense→Reason→Actuate→Reflect',
      'Human-in-the-loop',
      'Cryptographic audit',
    ],
    highlighted: true,
  },
  {
    badge: 'Layer 2',
    icon: Database,
    title: 'Data Intelligence (Pulse)',
    quote: 'We make it understandable and trustworthy.',
    items: ['Governed namespace', 'Schema enforcement', 'Computed KPIs', 'Auditable governance'],
  },
  {
    badge: 'Layer 1',
    icon: ServerIcon,
    title: 'Data Streaming (Broker + Edge)',
    quote: 'We move operational data reliably.',
    items: ['Enterprise MQTT', 'Edge protocol bridge', 'OT/IT integrations', 'Broker fan-out'],
  },
]

function HiveMQAgenticPage() {
  const _navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        centered
        title="HiveMQ Agentic AI for Operations"
        subtitle="Build and govern industrial AI agents where data lives - autonomous agents monitor operations, detect anomalies, and trigger corrective workflows, safely and in a closed loop."
      />

      <RevealStack align="stretch" gap={10}>
        {/* Section 1: Capabilities */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="An AI Agent Platform for Industrial Operations"
            description="Safe, closed-loop AI agents that run where your operational data already lives."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {capabilities.map((capability) => (
              <InsightCard
                key={capability.title}
                accent={capability.accent}
                colorPalette={capability.colorPalette}
                icon={capability.icon}
                badgeLabel="Capability"
                title={capability.title}
                description={capability.description}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Section 2: The Runtime Loop */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="The Runtime Loop"
            description="Every agent cycles through the same outer loop, cycle after cycle."
          />
          <HStack align="stretch" gap={3} flexWrap="wrap" justify="center">
            {outerLoop.map((stage, i) => (
              <HStack key={stage.title} align="stretch" gap={3} flex="1" minW="220px">
                <LoopStageCard {...stage} />
                {i < outerLoop.length - 1 ? <LoopArrow /> : null}
              </HStack>
            ))}
          </HStack>

          <Card.Root bg="bg.subtle" mt={2}>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="sm">Inside Reason</Heading>
                <HStack align="stretch" gap={3} flexWrap="wrap" justify="center">
                  {innerLoop.map((stage, i) => (
                    <HStack key={stage.title} align="stretch" gap={3} flex="1" minW="200px">
                      <LoopStageCard {...stage} />
                      {i < innerLoop.length - 1 ? <LoopArrow /> : null}
                    </HStack>
                  ))}
                </HStack>
                <Box borderLeftWidth="4px" borderLeftColor="brand.solid" pl={4} py={1}>
                  <Text fontSize="sm" color="content.secondary">
                    The LLM proposes; Govern and Authorise validate. Out-of-lane actions never reach
                    Actuate without a human decision. Every sub-stage appends to the tamper-evident
                    decision history - a regulator can replay any cycle end to end.
                  </Text>
                </Box>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Section 3: The Development Cycle */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="The Development Cycle"
            description="Where domain experts build, test, deploy, govern, and observe agents."
          />
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} gap={4}>
            {devCycle.map((stage) => (
              <Card.Root
                key={stage.title}
                transition="all 0.3s"
                _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              >
                <Card.Body p={5}>
                  <VStack align="start" gap={3}>
                    <Box p={2} bg="brand.solid" borderRadius="lg">
                      <stage.icon size={20} />
                    </Box>
                    <VStack align="start" gap={1}>
                      <Text fontWeight="bold">{stage.title}</Text>
                      <Text fontSize="sm" color="content.secondary">
                        {stage.description}
                      </Text>
                    </VStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            ))}
          </SimpleGrid>
          <Text fontSize="sm" color="content.tertiary" fontStyle="italic">
            Teams iterate in the outer loop (Build → Govern → Observe); agents iterate in the inner
            loop (Sense → Reason → Actuate → Reflect).
          </Text>
        </VStack>

        {/* Section 4: Part of the Unified Platform */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Part of the Unified Platform"
            description="Agentic AI is the newest layer of the HiveMQ Industrial Data Platform."
          />
          <VStack align="stretch" gap={4}>
            {platformLayers.map((layer) => (
              <Card.Root
                key={layer.badge}
                borderLeftWidth="4px"
                borderLeftColor={layer.highlighted ? 'brand.solid' : 'border'}
                bg={layer.highlighted ? 'bg.subtle' : undefined}
              >
                <Card.Body p={6}>
                  <VStack align="start" gap={3}>
                    <HStack gap={3}>
                      <Box p={2} bg="brand.solid" borderRadius="lg">
                        <layer.icon size={20} />
                      </Box>
                      <VStack align="start" gap={0}>
                        <HStack gap={2}>
                          <Badge
                            colorPalette={layer.highlighted ? 'brand' : 'gray'}
                            variant="subtle"
                          >
                            {layer.badge}
                          </Badge>
                          {layer.highlighted ? (
                            <Badge colorPalette="brand" variant="solid">
                              Current Focus
                            </Badge>
                          ) : null}
                        </HStack>
                        <Heading size="md">{layer.title}</Heading>
                      </VStack>
                    </HStack>
                    <Text fontSize="sm" fontStyle="italic" color="content.secondary">
                      &ldquo;{layer.quote}&rdquo;
                    </Text>
                    <Text fontSize="sm" color="content.secondary">
                      {layer.items.join(' · ')}
                    </Text>
                  </VStack>
                </Card.Body>
              </Card.Root>
            ))}
          </VStack>
        </VStack>
      </RevealStack>
    </Box>
  )
}
